import { useCallback, useEffect, useRef, useState } from 'react';

import { Edit as EditIcon } from '@mui/icons-material';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	ListItemText,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import {
	AddPropertyV2Model,
	EnumModel,
	EnumPropertyModel,
	NewEntityAsNewPropertyRequest,
	PrimitiveType,
	PropertyTypes,
	SchemaHierarchyModel,
	SchemaPropertyModel,
	SchemaSummaryModel,
	SimplePropertyModel,
	UpdatePropertyModel,
} from '../../../api/api';
import { getValidationErrorMessage, parseValidationError } from '../../../api/ApiValidationUtils';
import Spinner from '../../../common/components/Spinner';
import useToastService from '../../../common/hooks/useToastService';
import { useEntityMutations } from '../Hooks/useEntityController';
import { useEnumController } from '../Hooks/useEnumController';
import { EditSchemaPropertyDialog, Property } from './EditSchemaPropertyDialog';
import PropertyTable from './PropertyTable';

interface Props {
	schema: SchemaSummaryModel;
	hideSpinner?: boolean;
	onLoadingChange?: (loading: boolean) => void;
	availableEnums?: EnumModel[];
	fillAvailableHeight?: boolean;
	refreshVersion?: number;
}

const SchemaSection: React.FC<Props> = ({
	schema,
	hideSpinner = false,
	onLoadingChange,
	availableEnums,
	fillAvailableHeight = false,
	refreshVersion,
}) => {
	const toastService = useToastService();
	const [schemaDetails, setSchemaDetails] = useState<SchemaHierarchyModel | null>(null);
	const lastRefreshVersion = useRef(refreshVersion);
	const schemaRequestVersion = useRef(0);
	const [editPropertyOpen, setEditPropertyOpen] = useState(false);
	const [isWorking, setIsWorking] = useState(false);
	const [editingProperty, setEditingProperty] = useState<Property>({
		name: '',
		newName: '',
		description: '',
		isMultiple: false,
		isRequired: false,
		type: PrimitiveType.String,
		defaultValue: '',
		propertyType: PropertyTypes.Simple,
		enumId: '',
	});

	const [editDescOpen, setEditDescOpen] = useState(false);
	const [descDraft, setDescDraft] = useState('');

	const {
		getEntityById,
		addProperty,
		deleteProperty,
		addSchemaProperty,
		addReferenceProperty,
		addEnumProperty,
		propertyTypes,
		updateProperty,
		updateEntityDescription,
	} = useEntityMutations(schema.contextId);

	const { enums: loadedEnums } = useEnumController(schema?.contextId, { enabled: !availableEnums });
	const enums = availableEnums ?? loadedEnums;

	const getEntityDetails = useCallback(async () => {
		const requestVersion = ++schemaRequestVersion.current;
		const details = await getEntityById(schema?.id);
		if (schemaRequestVersion.current === requestVersion) {
			setSchemaDetails(details);
		}
		return details;
	}, [getEntityById, schema?.id]);

	useEffect(() => {
		schemaRequestVersion.current += 1;
		setSchemaDetails(null);
	}, [schema?.id]);

	useEffect(() => {
		if (refreshVersion === undefined || lastRefreshVersion.current === refreshVersion) {
			return;
		}

		lastRefreshVersion.current = refreshVersion;
		void getEntityDetails().catch((error: unknown) => {
			console.error('Failed to refresh schema details:', error);
		});
	}, [getEntityDetails, refreshVersion, schema.id]);

	useEffect(() => {
		onLoadingChange?.(isWorking);
	}, [isWorking, onLoadingChange]);

	useEffect(() => {
		if (schemaDetails) {
			return;
		}

		const loadDetails = async () => {
			try {
				setIsWorking(true);
				await getEntityDetails();
			} finally {
				setIsWorking(false);
			}
		};

		void loadDetails();
	}, [getEntityDetails, schemaDetails, schema?.id]);

	const handleAddProperty = async (property: SimplePropertyModel) => {
		try {
			setIsWorking(true);
			await addProperty(schema?.id ?? '', property);
			await getEntityDetails();
		} finally {
			setIsWorking(false);
		}
	};

	const handleAddSchemaProperty = async (property: NewEntityAsNewPropertyRequest) => {
		try {
			setIsWorking(true);
			await addSchemaProperty(schema?.id ?? '', property);
			await getEntityDetails();
		} finally {
			setIsWorking(false);
		}
	};

	const handleAddReferenceProperty = async (property: AddPropertyV2Model) => {
		try {
			setIsWorking(true);
			await addReferenceProperty(schema?.id ?? '', property);
			await getEntityDetails();
		} finally {
			setIsWorking(false);
		}
	};

	const handleAddEnumProperty = async (property: EnumPropertyModel) => {
		try {
			setIsWorking(true);
			await addEnumProperty(schema?.id ?? '', property);
			await getEntityDetails();
		} finally {
			setIsWorking(false);
		}
	};

	const handleDeleteProperty = async (propertyName: string) => {
		try {
			setIsWorking(true);
			await deleteProperty(schema?.id ?? '', propertyName);
			await getEntityDetails();
			toastService.success(`Property "${propertyName}" deleted.`);
		} catch (error) {
			const validationError = parseValidationError(error);
			const errorMessage = validationError
				? getValidationErrorMessage(validationError)
				: error instanceof Error
					? error.message
					: 'An unexpected error occurred.';
			toastService.error(`Failed to delete property "${propertyName}": ${errorMessage}`);
		} finally {
			setIsWorking(false);
		}
	};

	const handleOnEditProperty = async (property: SchemaPropertyModel) => {
		setEditingProperty({
			name: property.name,
			newName: property.name,
			description: property.description,
			isMultiple: property.isMultiple,
			isRequired: property.isRequired,
			type: property.type,
			enumId: property.enumId,
			enumContextId: property.enumContextId,
			defaultValue: property.defaultValue,
			propertyType: property.isComplex ? PropertyTypes.Complex : property.isEnum ? PropertyTypes.Enum : PropertyTypes.Simple,
		});

		setEditPropertyOpen(true);
	};

	const handleSaveUpdateProperty = async (property: Property) => {
		const updatePropertyModel: UpdatePropertyModel = {
			newName: property.newName,
			description: property.description,
			isMultiple: property.isMultiple,
			isRequired: property.isRequired,
			defaultValue: property.defaultValue ?? '',
			enumId: property.enumId,
			enumContextId: property.enumContextId,
		};

		try {
			setIsWorking(true);
			await updateProperty(schema?.id ?? '', property.name, property.propertyType, updatePropertyModel);
			setEditPropertyOpen(false);
			await getEntityDetails();
		} finally {
			setIsWorking(false);
		}
	};

	const openEditDescription = () => {
		const current = (schemaDetails?.description ?? schema.description) || '';
		setDescDraft(current);
		setEditDescOpen(true);
	};

	const handleSaveDescription = async () => {
		try {
			setIsWorking(true);
			await updateEntityDescription(schema?.id ?? '', descDraft.trim());
			setEditDescOpen(false);
			await getEntityDetails();
		} finally {
			setIsWorking(false);
		}
	};
	// ----------------------------------------------------------

	// Prefer details (when loaded) so header reflects latest changes post-save
	const headerDescription = schemaDetails?.description ?? schema.description;
	const propertyTable = (
		<PropertyTable
			onDelete={handleDeleteProperty}
			onAddProperty={handleAddProperty}
			onAddSchemaProperty={handleAddSchemaProperty}
			onAddReferenceProperty={handleAddReferenceProperty}
			onAddEnumProperty={handleAddEnumProperty}
			onEditProperty={handleOnEditProperty}
			contextId={schema.contextId}
			properties={schemaDetails?.properties ?? []}
			availableEnums={enums}
			propertyTypes={propertyTypes}
			fillAvailableHeight={fillAvailableHeight}
		/>
	);

	return (
		<>
			{isWorking && !hideSpinner && <Spinner />}
			<Box sx={{ ...(fillAvailableHeight && { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }) }}>
				<Box
					sx={{
						borderRadius: 1,
						backgroundColor: 'background.paper',
						pl: 2,
						flex: '0 0 auto',
						minHeight: 'auto',
						py: 1,
						'&:hover': {
							backgroundColor: 'rgba(255, 255, 255, 0.05)',
						},
					}}
				>
					<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
						<ListItemText
							primary={<Typography variant="h6">{schema.name}</Typography>}
							secondary={<Typography variant="body2">{headerDescription}</Typography>}
						/>
						<Tooltip title="Edit description">
							<IconButton
								size="small"
								aria-label="edit description"
								onClick={(e) => {
									e.stopPropagation();
									openEditDescription();
								}}
							>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>

				{fillAvailableHeight ? (
					<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>{propertyTable}</Box>
				) : (
					propertyTable
				)}
			</Box>

			<EditSchemaPropertyDialog
				open={editPropertyOpen}
				onClose={() => setEditPropertyOpen(false)}
				property={editingProperty!}
				onSave={handleSaveUpdateProperty}
				enumModels={enums}
			/>

			<Dialog open={editDescOpen} onClose={() => setEditDescOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Edit description</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="Description"
						fullWidth
						multiline
						minRows={3}
						value={descDraft}
						onChange={(e) => setDescDraft(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditDescOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={handleSaveDescription}
						disabled={descDraft.trim() === ((schemaDetails?.description ?? schema.description) || '').trim()}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SchemaSection;
